<template>
  <div class="alice-shell theme-turquoise">
    <TopBar class="alice-shell__topbar" />

    <Sidebar class="alice-shell__sidebar" variant="rail" active-key="mcp" />

    <main class="alice-shell__main">
      <PageHeader :title="guide.title" :subtitle="guide.lead" />

      <div class="mcp-guide" lang="nl">
        <nav class="mcp-guide__toc" aria-label="Op deze pagina">
          <div class="mcp-guide__toc-label">Op deze pagina</div>
          <ol class="mcp-guide__toc-list">
            <li v-for="item in guide.toc" :key="item.id">
              <a
                :href="`#${item.id}`"
                class="mcp-guide__toc-link"
                @click.prevent="scrollToSection(item.id)"
                >{{ item.text }}</a
              >
            </li>
          </ol>
        </nav>

        <!-- The guide is our own Markdown (src/content/mcp-guide.md), escaped
             by the renderer; only its whitelisted tags reach the page. -->
        <!-- eslint-disable-next-line vue/no-v-html -->
        <article class="mcp-guide__body" @click="onBodyClick" v-html="html" />
      </div>
    </main>
  </div>
</template>

<script>
import TopBar from "@/components/alice/TopBar.vue";
import Sidebar from "@/components/alice/Sidebar.vue";
import PageHeader from "@/components/alice/PageHeader.vue";
import { copyText } from "@/utils/clipboard";
import { parseGuide, COPY_LABEL } from "@/utils/guideMarkdown";
// The text of this page. Edit it there, not here.
import guideSource from "@/content/mcp-guide.md";

const GUIDE = parseGuide(guideSource);
const COPIED_LABEL = "Gekopieerd";

export default {
  name: "McpGuidePage",
  components: { TopBar, Sidebar, PageHeader },
  computed: {
    guide() {
      return GUIDE;
    },
    html() {
      return GUIDE.html;
    },
  },
  created() {
    this.copyTimers = new Map();
  },
  mounted() {
    // Deep link such as /mcp#toevoegen-aan-claude: open at that section.
    const id = (this.$route.hash || "").slice(1);
    if (id) this.$nextTick(() => this.scrollToSection(id, false));
  },
  beforeUnmount() {
    this.copyTimers.forEach((timer) => clearTimeout(timer));
  },
  methods: {
    scrollToSection(id, smooth = true) {
      // Ids are slugs (a-z, 0-9, -); anything else is not one of ours.
      if (!/^[a-z0-9-]+$/.test(id)) return;
      const target = this.$el.querySelector(`[id="${id}"]`);
      if (!target) return;
      target.scrollIntoView({
        behavior: smooth ? "smooth" : "auto",
        block: "start",
      });
      if (this.$route.hash !== `#${id}`) {
        this.$router.replace({ hash: `#${id}` });
      }
    },
    // One listener for everything inside the rendered guide: copy buttons on
    // code blocks, links to a section on this page, and links to other pages
    // in Alice (routed client-side instead of reloading the app).
    async onBodyClick(event) {
      const copyButton = event.target.closest(".guide-code__copy");
      if (copyButton) {
        const code = copyButton.closest(".guide-code").querySelector("code");
        if (await copyText(code ? code.textContent : "")) {
          this.showCopied(copyButton);
        }
        return;
      }
      const link = event.target.closest("a");
      if (!link) return;
      const href = link.getAttribute("href") || "";
      if (href.startsWith("#")) {
        event.preventDefault();
        this.scrollToSection(href.slice(1));
      } else if (link.dataset.internal) {
        event.preventDefault();
        this.$router.push(href);
      }
    },
    showCopied(button) {
      button.textContent = COPIED_LABEL;
      button.classList.add("is-copied");
      clearTimeout(this.copyTimers.get(button));
      this.copyTimers.set(
        button,
        setTimeout(() => {
          button.textContent = COPY_LABEL;
          button.classList.remove("is-copied");
          this.copyTimers.delete(button);
        }, 1500)
      );
    },
  },
};
</script>

<style scoped>
.alice-shell {
  display: grid;
  grid-template-rows: 56px 1fr;
  grid-template-columns: 56px 1fr;
  height: 100vh;
  width: 100%;
  background: var(--vp-white);
  font-family: var(--font-body);
  color: var(--fg-1);
  overflow: hidden;
}
.alice-shell__topbar {
  grid-column: 1 / -1;
  grid-row: 1;
}
.alice-shell__sidebar {
  grid-column: 1;
  grid-row: 2;
}
.alice-shell__main {
  grid-column: 2;
  grid-row: 2;
  min-width: 0;
  overflow-y: auto;
  background: var(--vp-white);
}

/* —— Layout: text column + table of contents ——
   The contents sit beside the text on wide screens (sticky while you
   scroll) and above it on narrow ones. */
.mcp-guide {
  display: grid;
  grid-template-columns: minmax(0, 720px) 220px;
  gap: 56px;
  padding: 32px 32px 96px;
}
.mcp-guide__toc {
  grid-column: 2;
  grid-row: 1;
  align-self: start;
  position: sticky;
  top: 32px;
}
.mcp-guide__body {
  grid-column: 1;
  grid-row: 1;
  min-width: 0;
}
@media (max-width: 1100px) {
  .mcp-guide {
    grid-template-columns: minmax(0, 720px);
    gap: 24px;
  }
  .mcp-guide__toc {
    grid-column: 1;
    position: static;
    padding: 16px 20px;
    background: var(--bg-muted);
  }
  .mcp-guide__body {
    grid-row: 2;
  }
}
@media (max-width: 600px) {
  .mcp-guide {
    padding: 20px 16px 64px;
  }
  /* Line the page header up with the text on phones. */
  .alice-shell__main > .alice-pageheader {
    padding-right: 16px;
    padding-left: 16px;
  }
}

.mcp-guide__toc-label {
  margin-bottom: 10px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.mcp-guide__toc-list {
  margin: 0;
  padding: 0;
  list-style: none;
}
.mcp-guide__toc-link {
  display: block;
  padding: 5px 0 5px 12px;
  border-left: 2px solid var(--border-hairline);
  font-family: var(--font-ui);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  color: var(--fg-2);
  text-decoration: none;
  transition: color var(--dur-fast) var(--ease-hover),
    border-color var(--dur-fast) var(--ease-hover);
}
.mcp-guide__toc-link:hover {
  color: var(--vp-black);
  border-left-color: var(--accent-100);
}

/* —— The rendered guide (v-html, hence :deep) —— */
.mcp-guide__body {
  font-family: var(--font-body);
  font-size: 17px;
  line-height: 1.6;
  color: var(--fg-1);
}
.mcp-guide__body :deep(h2) {
  margin: 48px 0 12px;
  padding-top: 32px;
  border-top: 1px solid var(--border-hairline);
  font-family: var(--font-display);
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
  color: var(--vp-black);
  scroll-margin-top: 16px;
}
.mcp-guide__body :deep(h2:first-child) {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}
.mcp-guide__body :deep(h3) {
  margin: 28px 0 8px;
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--vp-black);
  scroll-margin-top: 16px;
}
.mcp-guide__body :deep(p) {
  margin: 0 0 14px;
}
.mcp-guide__body :deep(ul),
.mcp-guide__body :deep(ol) {
  margin: 0 0 18px;
  padding-left: 24px;
}
.mcp-guide__body :deep(li) {
  margin: 6px 0;
  padding-left: 4px;
}
.mcp-guide__body :deep(li::marker) {
  color: var(--fg-3);
}
.mcp-guide__body :deep(ol > li::marker) {
  font-family: var(--font-ui);
  font-weight: 700;
  color: var(--accent-160);
}
.mcp-guide__body :deep(strong) {
  font-weight: 700;
}
.mcp-guide__body :deep(a) {
  color: var(--vp-black);
  text-decoration: underline;
  text-decoration-color: var(--accent-100);
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;
}
.mcp-guide__body :deep(a:hover) {
  text-decoration-color: var(--vp-black);
}
.mcp-guide__body :deep(code) {
  padding: 1px 6px;
  background: var(--bg-muted);
  font-family: var(--font-mono);
  font-size: 0.82em;
  overflow-wrap: anywhere;
}

/* Code blocks: the server address and the Claude Code command. */
.mcp-guide__body :deep(.guide-code) {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0 0 18px;
  padding: 12px 12px 12px 18px;
  background: var(--vp-black);
  color: var(--vp-white);
}
.mcp-guide__body :deep(.guide-code pre) {
  flex: 1;
  min-width: 0;
  margin: 0;
  padding-top: 6px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-family: var(--font-mono);
  font-size: 15px;
  line-height: 1.5;
}
.mcp-guide__body :deep(.guide-code code) {
  padding: 0;
  background: none;
  color: inherit;
  font-size: inherit;
}
.mcp-guide__body :deep(.guide-code__copy) {
  flex-shrink: 0;
  /* Wide enough for "Gekopieerd", so the button doesn't jump. */
  min-width: 104px;
  padding: 6px 12px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: transparent;
  color: var(--vp-white);
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-hover),
    transform var(--dur-press) var(--ease-out);
}
.mcp-guide__body :deep(.guide-code__copy:hover) {
  background: rgba(255, 255, 255, 0.12);
}
.mcp-guide__body :deep(.guide-code__copy:active) {
  transform: scale(0.97);
}
.mcp-guide__body :deep(.guide-code__copy.is-copied) {
  border-color: var(--accent-100);
  background: var(--accent-100);
  color: var(--vp-black);
}

/* Callouts (> [!TIP] … in the Markdown). */
.mcp-guide__body :deep(.guide-callout) {
  margin: 0 0 18px;
  padding: 14px 18px;
  border-left: 4px solid var(--vp-grijs-60);
  background: var(--vp-grijs-10);
}
.mcp-guide__body :deep(.guide-callout__label) {
  display: block;
  margin-bottom: 4px;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--vp-grijs-160);
}
.mcp-guide__body :deep(.guide-callout > :last-child) {
  margin-bottom: 0;
}
.mcp-guide__body :deep(.guide-callout--tip) {
  border-left-color: var(--vp-turquoise-100);
  background: var(--vp-turquoise-10);
}
.mcp-guide__body :deep(.guide-callout--tip .guide-callout__label) {
  color: var(--vp-turquoise-160);
}
.mcp-guide__body :deep(.guide-callout--important) {
  border-left-color: var(--vp-blauw-100);
  background: var(--vp-blauw-10);
}
.mcp-guide__body :deep(.guide-callout--important .guide-callout__label) {
  color: var(--vp-blauw-160);
}
.mcp-guide__body :deep(.guide-callout--warning) {
  border-left-color: var(--vp-geel-100);
  background: var(--vp-geel-10);
}
.mcp-guide__body :deep(.guide-callout--warning .guide-callout__label) {
  color: var(--vp-geel-160);
}
.mcp-guide__body :deep(.guide-callout--caution) {
  border-left-color: var(--vp-rood-100);
  background: var(--vp-rood-10);
}
.mcp-guide__body :deep(.guide-callout--caution .guide-callout__label) {
  color: var(--vp-rood-160);
}
</style>
